import * as React from 'react';


export type UseInViewportOptions = {
  /**
   * Delay (ms) after an element enters the viewport before `isInViewport`
   * flips true. Filters out fast-scroll flickers — if the element leaves the
   * viewport before the delay elapses, nothing fires. Defaults to 200ms.
   */
  settleMs?: number;
  /**
   * IntersectionObserver threshold. `0` fires as soon as any edge intersects;
   * `1` requires the full element to be visible. Defaults to `0`.
   */
  threshold?: number;
  /**
   * Margin applied to the root (viewport) bounds when computing intersection.
   * Use positive values to trigger early (e.g. `'200px 0px'` starts tracking
   * 200px before the element scrolls into view). Defaults to `'0px'`.
   */
  rootMargin?: string;
  /**
   * Optional scroll container. Defaults to the browser viewport when omitted.
   */
  root?: Element | Document | null;
};

export type UseInViewportResult = {
  /** Ref callback — attach to the element whose visibility you want to track. */
  ref: (el: Element | null) => void;
  /**
   * True once the element has been continuously intersecting for `settleMs`.
   * Flips back to false the moment it leaves the viewport (and cancels any
   * pending settle).
   */
  isInViewport: boolean;
};

/**
 * Visibility tracker backed by IntersectionObserver, with a settle delay so
 * fast scrolls don't trigger work for elements the user never actually saw.
 *
 * Typical use: gate an API call on visibility so an N-tile list only fetches
 * for tiles the user scrolls to.
 *
 * Example:
 *   const {ref, isInViewport} = useInViewport({settleMs: 200});
 *   const {data} = useQuery({
 *     queryKey: [...],
 *     enabled: isInViewport,
 *     queryFn: async ({signal}) => fetchThing({signal}),
 *   });
 *   return <div ref={ref}>...</div>;
 *
 */
export function useInViewport(
  options: UseInViewportOptions = {},
): UseInViewportResult {
  const {settleMs = 200, threshold = 0, rootMargin = '0px', root} = options;

  const [isInViewport, setIsInViewport] = React.useState(false);

  const observerRef = React.useRef<IntersectionObserver | null>(null);
  const elementRef = React.useRef<Element | null>(null);
  const settleTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const clearSettleTimer = React.useCallback(() => {
    if (settleTimerRef.current != null) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);

  const handleIntersection = React.useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      if (entry.isIntersecting) {
        if (settleTimerRef.current != null) {
          return;
        }
        settleTimerRef.current = setTimeout(() => {
          settleTimerRef.current = null;
          setIsInViewport(true);
        }, settleMs);
      } else {
        clearSettleTimer();
        setIsInViewport(false);
      }
    },
    [settleMs, clearSettleTimer],
  );

  // Rebuild the observer when its options change (rebind to the same element).
  React.useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }
    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold,
      rootMargin,
      root: root ?? null,
    });
    if (elementRef.current) {
      observerRef.current.observe(elementRef.current);
    }
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [handleIntersection, threshold, rootMargin, root]);

  const ref = React.useCallback(
    (el: Element | null) => {
      if (elementRef.current === el) {
        return;
      }
      if (elementRef.current && observerRef.current) {
        observerRef.current.unobserve(elementRef.current);
      }
      // Element swap / detach — drop pending settle so stale intent from the
      // previous element doesn't leak onto the new one.
      clearSettleTimer();
      setIsInViewport(false);
      elementRef.current = el;
      if (!el) {
        return;
      }
      if (typeof IntersectionObserver === 'undefined') {
        // No IO support — treat as visible so dependent work still runs.
        setIsInViewport(true);
        return;
      }
      observerRef.current?.observe(el);
    },
    [clearSettleTimer],
  );

  React.useEffect(() => {
    return () => {
      clearSettleTimer();
    };
  }, [clearSettleTimer]);

  return {ref, isInViewport};
}
